import { auth } from '../auth.js';
import { router } from '../router.js';
import { escapeHtml } from '../utils.js';
import { onChange as onEventsChange, getEvents, sidebarEvents, isLive, eventHref } from '../events-nav.js';

/**
 * Collapsible sidebar, ported from v1. Differences from v1: no Trading link (dropped from
 * the rebuild), and admin visibility comes from the Cognito `admins` group rather than a
 * database column. Chat RLS and its saved-chat list were removed on 2026-10-08.
 */

const COLLAPSE_KEY = 'sidebarCollapsed';

onEventsChange(() => {
    if (document.querySelector('.sidebar')) renderNavigation();
});

/**
 * First letters of the first two words. The result is escaped at the call site: it is
 * derived from a user-supplied name, and a name beginning with `<` would otherwise put a
 * bare `<` into the sidebar's markup. Two characters is not enough to build an attack out
 * of, but this was the one interpolation in the frontend not going through escapeHtml,
 * and an unescaped path is worth closing on principle rather than on exploitability.
 */
function getInitials(name) {
    if (!name) return '?';
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

/**
 * The user's events, listed under the Events link. Live ones first and marked as such, then
 * the rest newest-first -- an event page stays available once it exists, so past ones belong
 * here too.
 *
 * Renders from cache and never awaits: null means "not fetched yet", so it kicks off a fetch
 * and the onChange subscription above redraws when it lands.
 */
function activeEventList(path) {
    const events = sidebarEvents();
    if (events === null) {
        void getEvents();
        return '';
    }
    if (events.shown.length === 0) return '';

    const rows = events.shown.map((ev) => {
        const href = eventHref(ev);
        const live = isLive(ev);
        return `
            <a href="${href}" data-link
               class="sidebar-event-link${path === href ? ' active' : ''}${live ? ' is-live' : ''}"
               title="${escapeHtml(ev.name)}${live ? ' — happening now' : ''}">
                <span class="sidebar-event-dot" aria-hidden="true"></span>
                <span class="sidebar-event-name">${escapeHtml(ev.name)}</span>
            </a>`;
    }).join('');

    const more = events.hidden > 0
        ? '<a href="/events" data-link class="sidebar-event-more">More…</a>'
        : '';

    return `<div class="sidebar-event-list">${rows}${more}</div>`;
}

function navLink(href, icon, label, active) {
    return `
        <a href="${href}" data-link class="sidebar-link${active ? ' active' : ''}">
            <i class="fa-solid ${icon}"></i>
            <span>${label}</span>
        </a>`;
}

export function hideNavigation() {
    document.querySelectorAll('.sidebar, .sidebar-toggle-mobile, .sidebar-scrim').forEach((el) => el.remove());
    document.body.classList.remove('has-sidebar', 'sidebar-open', 'sidebar-collapsed');
}

export function renderNavigation() {
    if (!auth.isAuthenticated) {
        hideNavigation();
        return;
    }

    const user = auth.getUser();
    const isAdmin = auth.isAdmin();
    const path = router.getCurrentPath();
    const isHome = path === '/' || path === '/home';

    document.querySelectorAll('.sidebar, .sidebar-toggle-mobile, .sidebar-scrim').forEach((el) => el.remove());

    const collapsed = localStorage.getItem(COLLAPSE_KEY) === '1';
    document.body.classList.add('has-sidebar');
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    document.body.classList.remove('sidebar-open');

    const toggle = document.createElement('button');
    toggle.className = 'sidebar-toggle-mobile';
    toggle.setAttribute('aria-label', 'Open menu');
    toggle.innerHTML = '<i class="fa-solid fa-bars"></i>';
    document.body.appendChild(toggle);

    const scrim = document.createElement('div');
    scrim.className = 'sidebar-scrim';
    document.body.appendChild(scrim);

    // The gallery's controls live in the drawer on mobile, where there's no room for
    // them in the page header. The gallery page binds these by id if present.
    const pageControls = path === '/gallery'
        ? `
            <div class="sidebar-page-controls">
                <div class="sidebar-section-label">Gallery</div>
                <button type="button" id="mobile-upload-btn" class="btn btn-primary btn-full-width">
                    <i class="fa-solid fa-upload"></i><span>Upload Images</span>
                </button>
                <div class="input-wrapper">
                    <input type="text" id="mobile-search-input" placeholder="Search captions...">
                </div>
                <select id="mobile-collection-filter" class="form-select">
                    <option value="">All Collections</option>
                </select>
            </div>`
        : '';

    const sidebar = document.createElement('aside');
    sidebar.className = 'sidebar';
    sidebar.innerHTML = `
        <div class="sidebar-header">
            <a href="/" data-link class="sidebar-brand">
                <span class="sidebar-brand-mark">S</span>
                <span class="sidebar-brand-text">Shanks Family</span>
            </a>
            <button type="button" class="sidebar-collapse-btn" aria-label="Collapse sidebar">
                <i class="fa-solid fa-angles-left"></i>
            </button>
        </div>
        <nav class="sidebar-nav">
            ${navLink('/', 'fa-house', 'Home', isHome)}
            ${navLink('/gallery', 'fa-images', 'Gallery', path === '/gallery')}
            ${navLink('/calendar', 'fa-calendar-week', 'Calendar', path === '/calendar')}
            ${navLink('/events', 'fa-calendar-days', 'Events', path === '/events')}
            ${activeEventList(path)}
            ${auth.isLimited() ? '' : navLink('/shared', 'fa-share-nodes', 'Shared with me', path === '/shared')}
            ${isAdmin ? navLink('/admin', 'fa-shield-halved', 'Admin', path === '/admin') : ''}
            ${pageControls}
        </nav>
        <div class="sidebar-footer">
            <a href="/profile" data-link class="sidebar-user${path === '/profile' ? ' active' : ''}" title="Profile">
                <span class="sidebar-avatar">${escapeHtml(getInitials(user?.full_name))}</span>
                <span class="sidebar-user-name">${escapeHtml(user?.full_name ?? '')}</span>
            </a>
            <button type="button" id="sidebar-logout-btn" class="sidebar-logout">
                <i class="fa-solid fa-right-from-bracket"></i><span>Logout</span>
            </button>
        </div>`;
    document.body.appendChild(sidebar);

    const closeDrawer = () => document.body.classList.remove('sidebar-open');
    toggle.addEventListener('click', () => document.body.classList.toggle('sidebar-open'));
    scrim.addEventListener('click', closeDrawer);
    sidebar.querySelectorAll('[data-link]').forEach((l) => l.addEventListener('click', closeDrawer));

    sidebar.querySelector('.sidebar-collapse-btn')?.addEventListener('click', () => {
        const nowCollapsed = !document.body.classList.contains('sidebar-collapsed');
        document.body.classList.toggle('sidebar-collapsed', nowCollapsed);
        localStorage.setItem(COLLAPSE_KEY, nowCollapsed ? '1' : '0');
    });

    document.getElementById('sidebar-logout-btn')?.addEventListener('click', async () => {
        closeDrawer();
        await auth.logout();
    });
}
